import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RequestError, type ReviewSummary, api } from '../../lib/api'
import { faDate, faNum } from '../platform/labels'

const STATUS_FA: Record<ReviewSummary['status'], { fa: string; badge: string }> = {
  submitted: { fa: 'در صف ارزیاب', badge: 'badge--stored' },
  in_review: { fa: 'در حال بازبینی', badge: 'badge--warn' },
  finalized: { fa: 'نهایی شده', badge: 'badge--good' },
  returned: { fa: 'برگشت داده شده', badge: 'badge--bad' },
}

const OUTCOME_FA: Record<NonNullable<ReviewSummary['outcome']>, string> = {
  awarded: 'نشان اعطا شد',
  conditional: 'نشان مشروط اعطا شد',
  none: 'این بار نشانی اعطا نشد',
}

/**
 * «ارسال برای بازبینی» and where it stands.
 *
 * Submitting is the organization's own decision, so only its admin sees the
 * button, and the server refuses it to anybody standing in for them. What the
 * assessor decides comes back here with their note — a «no» without a reason
 * is not something a charity can act on.
 */
export function ReviewCard() {
  const queryClient = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const review = useQuery({ queryKey: ['review'], queryFn: api.review })
  const [note, setNote] = useState('')
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = () => {
    setError(null)
    setOpen(false)
    void queryClient.invalidateQueries({ queryKey: ['review'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const fail = (err: unknown) => setError(err instanceof RequestError ? err.messageFa : 'انجام نشد.')
  const submit = useMutation({ mutationFn: () => api.submitReview(note.trim() || null), onSuccess: refresh, onError: fail })
  const withdraw = useMutation({ mutationFn: api.withdrawReview, onSuccess: refresh, onError: fail })

  const user = me.data?.user
  const canSubmit = user?.role === 'org_admin' && !user.impersonating
  const r = review.data?.review ?? null
  const isOpen = r !== null && (r.status === 'submitted' || r.status === 'in_review')

  return (
    <div className="card">
      <h2>بازبینی ارزیاب افرا</h2>
      <p className="meta">
        وقتی فکر می‌کنید تصویر مؤسسه کامل شده، آن را برای ارزیاب افرا بفرستید. ارزیاب هر مورد را کنار شواهدش بررسی
        می‌کند، امتیاز نهایی را می‌گذارد و دربارهٔ نشان تصمیم می‌گیرد.
      </p>

      {r && (
        <div className="stack" style={{ marginBottom: 'var(--space-3)' }}>
          <div>
            <span className={`badge ${STATUS_FA[r.status].badge}`}>{STATUS_FA[r.status].fa}</span>{' '}
            <span className="meta">فرستاده شده در {faDate(r.submittedOn)}</span>
          </div>
          {r.status === 'finalized' && r.outcome && (
            <div className="notice notice--info">
              <strong>{OUTCOME_FA[r.outcome]}</strong>
              {r.outcomeLevel !== null && <> · سطح <span className="num">{faNum(r.outcomeLevel)}</span></>}
              {r.outcomeNoteFa && <div>{r.outcomeNoteFa}</div>}
            </div>
          )}
          {r.status === 'returned' && r.outcomeNoteFa && (
            <div className="notice notice--info">
              <strong>یادداشت ارزیاب:</strong> {r.outcomeNoteFa}
            </div>
          )}
        </div>
      )}

      {error && <p className="notice notice--error">{error}</p>}

      {canSubmit && !isOpen && (
        open ? (
          <div className="stack">
            <div className="field">
              <label htmlFor="review-note">اگر نکته‌ای برای ارزیاب دارید بنویسید (اختیاری)</label>
              <textarea id="review-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="row">
              <button type="button" className="btn btn--sm" disabled={submit.isPending} onClick={() => submit.mutate()}>
                {submit.isPending ? 'در حال فرستادن…' : 'بفرست برای بازبینی'}
              </button>
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOpen(false)}>
                بی‌خیال
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
            {r?.status === 'finalized' || r?.status === 'returned' ? 'فرستادن دوباره برای بازبینی' : 'ارسال برای بازبینی'}
          </button>
        )
      )}
      {canSubmit && r?.status === 'submitted' && (
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          disabled={withdraw.isPending}
          onClick={() => withdraw.mutate()}
        >
          پس گرفتن ارسال
        </button>
      )}
      {!canSubmit && !isOpen && <p className="meta">فرستادن برای بازبینی با مدیر مؤسسه است.</p>}
    </div>
  )
}
